import {UIService} from "../UIService";
import {ProductDataService} from "../../services/productDataService";
import {ProductDataStore} from "../../stores/productDataStore";
import {InternalBlindLouvreService} from "./internalBlindLouvreService";
import {ColorService} from "../colorComponent/colorService";

export class InternalBlindLouvreUIService {
    private uiService: UIService;
    private productDataService: ProductDataService;
    private productDataStore: ProductDataStore;
    private internalBlindLouvreService: InternalBlindLouvreService;
    private colorService: ColorService;

    constructor() {
        this.uiService = new UIService();
        this.productDataService = new ProductDataService();
        this.productDataStore = new ProductDataStore();
        this.internalBlindLouvreService = new InternalBlindLouvreService();
        this.colorService = new ColorService();
    }

    public initializeInternalBlindLouvreSelector() {
        let internalBlindLouvres = this.productDataStore.getAvailableOptionsByName('internalBlindLouvres');

        internalBlindLouvres.forEach(internalBlindLouvre => {
            const option = this.createInternalBlindLouvreSelector(internalBlindLouvre);
            if (internalBlindLouvre.id === this.productDataStore.getSelectedOptionIdByName('internalBlindLouvre')) {
                this.uiService.setSelectedOption(option);
            }

            option.addEventListener('click', (event: any) => {
                this.productDataService.updateSelectedOptionByName('internalBlindLouvre', internalBlindLouvre.id, true);
                this.uiService.resetSelectionsByPartName('internalBlindLouvre');
                this.uiService.setSelectedOption(event.currentTarget);
                this.setColorPalettes();
            });
        });

        this.setColorPalettes();

        const resetButton = this.uiService.getResetButtonByModule('internalBlindLouvre');
        resetButton.addEventListener('click', this.resetSelectedOptions.bind(this));
    }

    private resetSelectedOptions() {
        this.internalBlindLouvreService.resetSelectedOptions();
        this.setColorPalettes();
    }

    private createInternalBlindLouvreSelector(louvre: any) {
        const option = this.uiService.createConfiguratorOption('internalBlindLouvre');

        this.uiService.setAttributeToElement(
            option,
            'nf-option-id',
            louvre.id
        );

        this.uiService.setAttributeToElement(
            this.uiService.getElementByAttribute('nf-value-key', 'img', option),
            'src',
            louvre.attributes.imgSrc
        );

        this.uiService.addTextInElement(
            this.uiService.getElementByAttribute('nf-value-key', 'name', option),
            louvre.attributes.name
        );

        const price = '+' + ((louvre.attributes.priceAfterDiscount) ?? 0) + ' kr';
        this.uiService.addTextInElement(
            this.uiService.getElementByAttribute('nf-value-key', 'price', option),
            price
        );

        return option;
    }

    private setColorPalettes() {
        const selectedPaletteId = this.productDataStore.getSelectedOptionIdByName('internalBlindLouvreColorPalette');
        const selectedOptionId = this.productDataStore.getSelectedOptionIdByName('internalBlindLouvre');
        const selectedOption = this.productDataStore.getAvailableOptionByNameAndId('internalBlindLouvres', selectedOptionId);

        let colorPalettes = this.productDataStore.getAvailableOptionsByName('colorPalettes');
        colorPalettes = colorPalettes.filter(palette => selectedOption.attributes.colorPalettes.indexOf(palette.id) > -1);

        this.uiService.removeElementsByPartName('internalBlindLouvreColorPalette');
        if (colorPalettes.length > 1) {
            colorPalettes.forEach(palette => {
                const option = this.createPaletteOption(palette);

                if (palette.id === selectedPaletteId) {
                    this.uiService.setSelectedOption(option);
                }

                option.addEventListener('click', (event: any) => {
                    this.setColorsInPalette(palette.id);
                    this.productDataService.updateSelectedOptionByName('internalBlindLouvreColorPalette', palette.id);
                    this.uiService.resetSelectionsByPartName('internalBlindLouvreColorPalette');
                    this.uiService.setSelectedOption(event.currentTarget);
                });
            });
        }

        this.setColorsInPalette(selectedPaletteId);

        this.uiService.getElementByAttribute('nf-template-action', 'internalBlindLouvreColorSearchBar').addEventListener('keyup', (event: any) => {
            const selectedPaletteId = this.productDataStore.getSelectedOptionIdByName('internalBlindLouvreColorPalette');
            this.setColorsInPalette(selectedPaletteId, event.currentTarget.value);
        });
    }

    private setColorsInPalette(paletteId, searchKeyword: string = null) {
        const selectedColorId = this.productDataStore.getSelectedOptionIdByName('internalBlindLouvreColor');
        let colorsInPalette = this.productDataStore.getAvailableOptionsByName('colors');
        colorsInPalette = colorsInPalette.filter(color => this.colorService.filterColorInPalette(color, paletteId, searchKeyword));

        this.uiService.removeElementsByPartName('internalBlindLouvreColors');
        colorsInPalette.forEach(color => {
            const option = this.createColorOption(color);

            if (color.id === selectedColorId) {
                this.uiService.setSelectedOption(option);
            }

            option.addEventListener('click', (event: any) => {
                this.productDataService.updateSelectedOptionByName('internalBlindLouvreColor', color.id, true);
                this.uiService.resetSelectionsByPartName('internalBlindLouvreColors');
                this.uiService.setSelectedOption(event.currentTarget);
            });
        });
    }

    private createPaletteOption(palette: any) {
        const option = this.uiService.createConfiguratorOption('internalBlindLouvreColorPalette');

        this.uiService.setAttributeToElement(
            option,
            'nf-option-id',
            palette.id
        );

        this.uiService.addTextInElement(
            option,
            palette.name
        );

        return option;
    }

    private createColorOption(color: any) {
        const option = this.uiService.createConfiguratorOption('internalBlindLouvreColor');

        this.uiService.setAttributeToElement(
            option,
            'nf-option-id',
            color.id
        );

        this.uiService.addTextInElement(
            option,
            (color.attributes.isRal) ? color.attributes.ral : color.attributes.name
        );

        const imgElement = this.uiService.getElementByAttribute('nf-value-key', 'img', option);
        const hexElement = this.uiService.getElementByAttribute('nf-value-key', 'hex', option);

        if (color.attributes.imgThumbSrc) {
            this.uiService.setAttributeToElement(
                imgElement,
                'src',
                color.attributes.imgThumbSrc
            );

            this.uiService.hideElement(hexElement);
        } else {
            this.uiService.setBackgroundColorToElement(
                hexElement,
                color.attributes.hex
            );

            this.uiService.hideElement(imgElement);
        }

        return option;
    }
}
